
import React, { useEffect, useState } from 'react';
import { SimulationConfig, AppSave } from './types';
import { SectionLabel } from './SectionLabel';
import { CloudService } from './CloudService';
import { STORAGE_KEY } from './constants';

interface StorageManagerProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const StorageManager: React.FC<StorageManagerProps> = ({ config, setConfig }) => {
  const [activeTab, setActiveTab] = useState<'LOCAL' | 'CLOUD'>('LOCAL');
  const [cloudSaves, setCloudSaves] = useState<AppSave[]>([]);
  const [saveName, setSaveName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localSaves, setLocalSaves] = useState<Record<string, SimulationConfig>>({});

  useEffect(() => {
    refreshSaves();
  }, [activeTab]);

  const refreshSaves = async () => {
    if (activeTab === 'CLOUD') {
      setIsLoading(true);
      const saves = await CloudService.getSaves();
      setCloudSaves(saves);
      setIsLoading(false);
    } else {
      // Load all keys starting with 'hypnotic_save_'
      const saves: Record<string, SimulationConfig> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('hypnotic_save_')) {
          try {
            saves[key.replace('hypnotic_save_', '')] = JSON.parse(localStorage.getItem(key)!);
          } catch (e) {}
        }
      }
      setLocalSaves(saves);
    }
  };

  const handleSave = async () => {
    const name = saveName.trim() || `Save ${new Date().toLocaleTimeString()}`;
    if (activeTab === 'CLOUD') {
      setIsLoading(true);
      await CloudService.saveConfig(name, config);
      setIsLoading(false);
      refreshSaves();
    } else {
      localStorage.setItem(`hypnotic_save_${name}`, JSON.stringify(config));
      refreshSaves();
    }
    setSaveName('');
  };

  const loadConfig = (savedConfig: SimulationConfig) => {
    if (window.confirm("Load this configuration? Current unsaved changes will be lost.")) {
      setConfig({ ...savedConfig });
    }
  };

  const deleteSave = async (idOrName: string) => {
    if (!window.confirm("Are you sure?")) return;
    if (activeTab === 'CLOUD') {
      await CloudService.deleteSave(idOrName);
    } else {
      localStorage.removeItem(`hypnotic_save_${idOrName}`);
    }
    refreshSaves();
  };

  const setAsDefault = async (id: string) => {
    if (activeTab === 'CLOUD') {
      await CloudService.updateSave(id, { isDefault: true });
      refreshSaves(); // refresh to show update
    } else {
      // Local default is just the main storage key
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
      alert("This configuration is now your local default.");
    }
  };

  const transferToCloud = async (name: string, savedConfig: SimulationConfig) => {
    const cloudName = prompt("Enter name for Cloud Save:", name);
    if (cloudName === null) return;

    setIsLoading(true);
    try {
        await CloudService.saveConfig(cloudName.trim() || name, savedConfig);
        if (window.confirm(`Successfully uploaded "${cloudName}" to Cloud. Switch to Cloud tab?`)) {
            setActiveTab('CLOUD');
        }
    } catch (e) {
        alert("Upload failed. Please check your connection.");
    }
    setIsLoading(false);
  };

  const exportAllData = () => {
    const data: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Include all keys that start with hypnotic_ to ensure all app data is captured
      if (key && key.startsWith('hypnotic_')) {
        data[key] = localStorage.getItem(key) || '';
      }
    }
    
    const backup = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      data: data
    };
    
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hypnotic_full_backup_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        const data = json.data || json; // Support both wrapped and unwrapped formats
        
        if (typeof data !== 'object' || data === null) throw new Error('Invalid format');
        
        const keys = Object.keys(data);
        if (keys.length === 0) {
            alert("The backup file appears to be empty.");
            return;
        }

        if (window.confirm(`Found ${keys.length} items in backup. This will overwrite existing local data. Continue?`)) {
          Object.entries(data).forEach(([key, value]) => {
            if (typeof value === 'string' && key.startsWith('hypnotic_')) {
              localStorage.setItem(key, value);
            }
          });
          refreshSaves();
          alert("Full system restore complete! Please refresh or reload to ensure all settings are applied.");
          window.location.reload(); // Force reload to apply all imported data correctly
        }
      } catch (err) {
        alert("Failed to import data: Invalid JSON file.");
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 p-4 rounded-3xl border bg-cyan-500/5 border-cyan-500/20">
        <SectionLabel icon="fa-database" darkMode={config.darkMode}>Storage Manager</SectionLabel>
        
        <div className={`grid grid-cols-2 gap-1 p-1 rounded-xl mb-2 ${config.darkMode ? 'bg-neutral-800' : 'bg-neutral-100'}`}>
          <button onClick={() => setActiveTab('LOCAL')} className={`text-[9px] font-black py-2 rounded-lg transition-all ${activeTab === 'LOCAL' ? 'bg-cyan-600 text-white shadow-sm' : 'text-neutral-500'}`}>Local Storage</button>
          <button onClick={() => setActiveTab('CLOUD')} className={`text-[9px] font-black py-2 rounded-lg transition-all ${activeTab === 'CLOUD' ? 'bg-cyan-600 text-white shadow-sm' : 'text-neutral-500'}`}>D1 Cloud</button>
        </div>

        <div className="flex gap-2 mb-2">
          <input 
            type="text" 
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            placeholder="New Save Name..."
            className={`flex-1 px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
          />
          <button onClick={handleSave} className="px-4 bg-cyan-600 text-white rounded-xl font-black text-[9px] uppercase hover:bg-cyan-700 shadow-lg shadow-cyan-500/20">
            Save
          </button>
        </div>

        <div className="flex flex-col gap-2 max-h-60 overflow-y-auto scrollbar-hide">
          {isLoading && <div className="text-center text-[10px] opacity-50 py-4">Connecting to Cloud...</div>}
          
          {activeTab === 'LOCAL' && Object.keys(localSaves).length === 0 && (
            <div className="text-center text-[10px] opacity-50 py-4">No local saves found.</div>
          )}
          
          {activeTab === 'LOCAL' && Object.entries(localSaves).map(([name, data]) => (
            <div key={name} className={`flex items-center justify-between p-2 rounded-lg border transition-all ${config.darkMode ? 'bg-neutral-800/40 border-neutral-700' : 'bg-white border-neutral-200'}`}>
               <span className="text-[10px] font-black truncate max-w-[100px]">{name}</span>
               <div className="flex gap-1">
                 <button onClick={() => loadConfig(data as SimulationConfig)} title="Load" className="px-2 py-1 bg-green-500/10 text-green-500 rounded text-[9px] font-black hover:bg-green-500/20">LOAD</button>
                 <button onClick={() => transferToCloud(name, data as SimulationConfig)} title="Upload to Cloud" className="px-2 py-1 bg-blue-500/10 text-blue-500 rounded text-[9px] hover:bg-blue-500/20"><i className="fa-solid fa-cloud-arrow-up"></i></button>
                 <button onClick={() => deleteSave(name)} title="Delete" className="px-2 py-1 bg-red-500/10 text-red-500 rounded text-[9px] hover:bg-red-500/20"><i className="fa-solid fa-trash"></i></button>
               </div>
            </div>
          ))}

          {activeTab === 'CLOUD' && cloudSaves.map(save => (
            <div key={save.id} className={`flex items-center justify-between p-2 rounded-lg border transition-all ${save.isDefault ? 'border-yellow-500/50 bg-yellow-500/5' : (config.darkMode ? 'bg-neutral-800/40 border-neutral-700' : 'bg-white border-neutral-200')}`}>
               <div className="flex flex-col">
                 <span className="text-[10px] font-black truncate max-w-[120px] flex items-center gap-2">
                   {save.name}
                   {save.isDefault && <i className="fa-solid fa-star text-yellow-500 text-[8px]"></i>}
                 </span>
                 <span className="text-[7px] opacity-40">{new Date(save.updatedAt).toLocaleDateString()}</span>
               </div>
               <div className="flex gap-1">
                 <button onClick={() => loadConfig(save.config as SimulationConfig)} title="Load" className="px-2 py-1 bg-green-500/10 text-green-500 rounded text-[9px] font-black hover:bg-green-500/20">LOAD</button>
                 <button onClick={() => setAsDefault(save.id)} title="Set as Cloud Default" className={`px-2 py-1 rounded text-[9px] hover:bg-yellow-500/20 ${save.isDefault ? 'bg-yellow-500 text-black' : 'bg-yellow-500/10 text-yellow-500'}`}><i className="fa-solid fa-star"></i></button>
                 <button onClick={() => deleteSave(save.id)} title="Delete" className="px-2 py-1 bg-red-500/10 text-red-500 rounded text-[9px] hover:bg-red-500/20"><i className="fa-solid fa-trash"></i></button>
               </div>
            </div>
          ))}
        </div>
        
        {activeTab === 'LOCAL' && (
          <button onClick={() => setAsDefault('')} className="mt-2 text-[8px] opacity-50 hover:opacity-100 uppercase font-black text-center w-full">Set Current as Auto-Load Default</button>
        )}
      </div>

      {/* BIG BACKUP SECTION */}
      <div className="flex flex-col gap-3 p-5 rounded-3xl border bg-amber-500/5 border-amber-500/20 shadow-xl shadow-amber-500/5">
        <div className="flex items-center justify-between">
          <SectionLabel icon="fa-file-export" darkMode={config.darkMode}>Full System Backup</SectionLabel>
          <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-500 text-[7px] font-black uppercase">Local JSON</span>
        </div>
        
        <p className="text-[9px] opacity-60 leading-relaxed">
          Download all your local saves, custom songs, and uploaded sounds into a single JSON file. You can restore this file later to recover all your data.
        </p>

        <div className="grid grid-cols-1 gap-2 mt-1">
          <button 
            onClick={exportAllData}
            className="flex items-center justify-center gap-3 py-3 bg-amber-600 text-white rounded-2xl text-[10px] font-black hover:bg-amber-700 transition-all shadow-lg shadow-amber-600/20 active:scale-95"
          >
            <i className="fa-solid fa-download text-xs"></i>
            DOWNLOAD FULL BACKUP (.JSON)
          </button>
          
          <div className="flex items-center gap-2 my-1">
            <div className="h-px flex-1 bg-amber-500/10" />
            <span className="text-[7px] font-black opacity-30 uppercase">OR RESTORE</span>
            <div className="h-px flex-1 bg-amber-500/10" />
          </div>

          <label className="flex items-center justify-center gap-3 py-3 bg-neutral-800 text-amber-500 rounded-2xl text-[10px] font-black hover:bg-neutral-700 transition-all border border-amber-500/20 cursor-pointer active:scale-95">
            <i className="fa-solid fa-upload text-xs"></i>
            UPLOAD & RESTORE BACKUP
            <input type="file" accept=".json" onChange={importData} className="hidden" />
          </label>
        </div>

        <div className="flex items-center gap-2 mt-1 opacity-40">
          <i className="fa-solid fa-circle-info text-[8px]"></i>
          <span className="text-[7px] font-bold italic">Restoring will overwrite current local data.</span>
        </div>
      </div>
    </div>
  );
};
